import { API } from "./tasks";
import { tx } from "../i18n";

/* ══ 로그인이 읽는 문 — `crates/api/src/accounts.rs` ══
   판정은 전부 서버다(`docs/decisions/auth.md` §4). 화면은 **서버가 준 문장을 그대로** 보여준다 —
   여기서 「비밀번호가 짧다」 같은 검사를 다시 하면 두 벌이 되고, 한쪽만 고쳐진다. */

export type Role = "admin" | "user";

/** `GET /auth/state` — 로그인 전에도 열린 문이다. */
export type LoginMode = "local" | "sso" | "local+sso";
export type AuthState = {
  setupRequired: boolean;
  loginMode: LoginMode;
  /** hub 주소 — 실패 문장에 「어디에 못 닿았는지」를 적으려고 받는다. SSO 를 안 쓰면 null. */
  ssoIssuer: string | null;
};

/** `GET /auth/me`. 에이전트 토큰으로 부르면 `kind: "agent"` 가 온다(화면은 늘 사람이다). */
export type Me = {
  kind: "human";
  id: string;
  username: string;
  displayName: string | null;
  role: Role;
  hasPassword: boolean;
  /** 2단계를 등록했나. 비밀 자체는 어떤 응답에도 없다. */
  totpEnabled: boolean;
  /** 정책이 이 세션에 2단계 등록을 요구하나 — 참인데 미등록이면 관문이 등록 문 밖을 403 으로 막는다. */
  totpRequired: boolean;
  /** 이번 세션이 어느 문으로 들어왔나. 못 읽었으면 null — 지어내지 않는다. */
  via: "local" | "sso" | null;
  expiresAt: string | null;
};

/** `GET /admin/users` 의 한 줄. */
export type UserRow = {
  id: string;
  username: string;
  displayName: string | null;
  role: Role;
  hasPassword: boolean;
  disabled: boolean;
  totpEnabled: boolean;
  /** hub 계정과 이어졌나(sub 자체는 안 온다). */
  hubLinked: boolean;
  email: string | null;
  /** 접근 범위 — null 은 전체, 빈 배열은 아무것도. 관리자는 이 값과 상관없이 전체다. */
  projects: string[] | null;
  createdAt: string;
};

export type TotpPolicy = "off" | "admin" | "all";

/** `POST /auth/login` 의 답 — 2단계를 등록한 계정은 세션 대신 대기 토큰이 온다(202). */
export type LoginResult = { username: string; role: Role } | { totpRequired: true; pendingToken: string };

/** 서버의 거절. `message` 가 사람이 읽을 문장이고, `code` 는 서버의 `error` 그대로다. */
export class AuthError extends Error {
  constructor(
    public status: number,
    public code: string,
    msg: string,
  ) {
    super(msg);
  }
}

export async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  let r: Response;
  try {
    r = await fetch(`${API}${path}`, {
      method,
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    // 연결 자체가 안 됐다 — 서버 문장이 없으니 **어디에** 못 닿았는지를 말한다.
    throw new AuthError(0, "unreachable", tx("auth.unreachable", { url: `${API}${path}`, reason: (e as Error).message }));
  }
  const data = (await r.json().catch(() => null)) as (T & { error?: string; message?: string }) | null;
  if (r.ok) return data as T;
  throw new AuthError(r.status, data?.error ?? `http ${r.status}`, data?.message ?? data?.error ?? `HTTP ${r.status} — ${path}`);
}

/** `GET /admin/runners` 한 줄(`crates/api/src/runners.rs`). 모르는 값은 `null` 이다 — 0 · 빈 값으로 채우지 않는다. */
export type RunnerPresence = {
  name: string;
  runner: string;
  /** 나루가 안 보냈으면 `null` — 빈 목록(「태그 없음」)과 다르다. */
  tags: string[] | null;
  vendors: string[];
  version: string;
  /** `host` · `docker` — 옛 나루는 안 보내 `null`(「모름」). */
  mode?: string | null;
  since: string;
};
export type RunnerRow = {
  name: string;
  source: "env" | "web";
  createdBy: string | null;
  createdAt: string | null;
  connections: number;
  presence: RunnerPresence | null;
  lastSeenAt: string | null;
  /** 끊긴 러너만 — 「적어도 이때부터 안 붙었다」(서버 `runner_watch` 가 정한다). 붙었거나 안 붙어 봤으면 null. */
  downSince?: string | null;
};

/** `GET /admin/runner-pair/:code` — 서버가 띄운 연결 요청(`crates/api/src/runner_pair.rs`). 이름 · 호스트는
 *  서버가 **댄** 값이라 확인된 것이 아니다. `nameTaken` 은 서버가 댄 이름을 지금 누가 쓰나다. */
export type RunnerPairRequest = {
  code: string;
  name: string;
  host: string | null;
  createdAt: string;
  expiresAt: string;
  approvedName: string | null;
  approvedBy: string | null;
  nameTaken: "env" | "web" | null;
};

export const authApi = {
  state: () => send<AuthState>("GET", "/auth/state"),
  me: () => send<Me | { kind: "agent"; name: string }>("GET", "/auth/me"),
  setup: (b: { token: string; username: string; password: string; displayName?: string }) =>
    send<{ username: string; role: Role }>("POST", "/auth/setup", b),
  /** `remember` — 「로그인 유지」. 켜면 쿠키 7일 · 쓰는 동안 이어진다(auth.md §3). */
  login: (b: { username: string; password: string; remember?: boolean }) => send<LoginResult>("POST", "/auth/login", b),
  loginTotp: (b: { pendingToken: string; code: string }) =>
    send<{ username: string; role: Role }>("POST", "/auth/login/totp", b),
  totpSetup: (password: string) => send<{ secret: string; uri: string }>("POST", "/auth/totp/setup", { password }),
  totpConfirm: (code: string) => send<{ totpEnabled: true }>("POST", "/auth/totp/confirm", { code }),
  totpDisable: (password: string) => send<{ totpEnabled: false }>("POST", "/auth/totp/disable", { password }),
  totpPolicy: () => send<{ policy: TotpPolicy }>("GET", "/admin/auth/totp-policy"),
  setTotpPolicy: (policy: TotpPolicy) => send<{ policy: TotpPolicy }>("PUT", "/admin/auth/totp-policy", { policy }),
  adminTotpReset: (id: string) => send<{ ok: true }>("DELETE", `/admin/users/${encodeURIComponent(id)}/totp`),
  logout: () => send<{ ok: true }>("POST", "/auth/logout", {}),
  changePassword: (b: { current: string; next: string }) => send<{ ok: true }>("POST", "/auth/password", b),
  users: () => send<UserRow[]>("GET", "/admin/users"),
  createUser: (b: { username: string; displayName?: string; role: Role; password: string; email?: string; projects?: string[] | null }) =>
    send<UserRow>("POST", "/admin/users", b),
  updateUser: (id: string, b: { role?: Role; disabled?: boolean; displayName?: string; email?: string; projects?: string[] | null }) =>
    send<UserRow>("PATCH", `/admin/users/${encodeURIComponent(id)}`, b),
  runners: () => send<{ runners: RunnerRow[] }>("GET", "/admin/runners").then((r) => r.runners),
  /** 원문 토큰은 이 답에만 있다 — 다시 읽는 문이 없다. */
  createRunner: (name: string) => send<{ name: string; token: string }>("POST", "/admin/runners", { name }),
  revokeRunner: (name: string) => send<null>("DELETE", `/admin/runners/${encodeURIComponent(name)}`),
  pairRequest: (code: string) => send<RunnerPairRequest>("GET", `/admin/runner-pair/${encodeURIComponent(code)}`),
  /** 허용은 이름만 적는다 — 토큰은 서버가 받아 갈 때 생기고 이 화면에는 안 온다. */
  approvePair: (code: string, name: string, replace: boolean) =>
    send<{ code: string; name: string }>("POST", `/admin/runner-pair/${encodeURIComponent(code)}/approve`, { name, replace }),
  denyPair: (code: string) => send<null>("DELETE", `/admin/runner-pair/${encodeURIComponent(code)}`),
};

export function authErrText(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

/** 세션이 끊겼다는 신호. 문(관문)이 401 을 내면 **어느 화면이든** 로그인으로 돌아가야 한다. */
export const LOGGED_OUT = "b3xin:logged-out";
/** 정책이 2단계 등록을 요구한다는 신호(관문의 403 `totp_enrollment_required`). 관리자가 정책을
 *  켜는 순간 **다음 요청부터** 걸리므로, 보던 화면에서 그대로 등록 화면으로 넘어간다. */
export const ENROLL_REQUIRED = "b3xin:totp-enroll-required";

/** `fetch` 를 **한 번만** 감싸 api 의 401 을 본다. 화면마다 401 을 따로 다루면 언젠가 한 곳이
 *  빠지고, 그 화면만 「불러오는 중…」에 영영 멈춘다(오류 글도 「login required」 한 줄이다).
 *
 *  `/auth/*` 는 빼고 본다 — 로그인 폼의 「틀렸다」(401)는 그 폼이 자기 문장으로 말할 일이다. */
export function watchUnauthorized(): void {
  const w = window as Window & { __b3xinAuthWatch?: boolean };
  if (w.__b3xinAuthWatch) return;
  w.__b3xinAuthWatch = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await orig(input, init);
    if (res.status === 401 || res.status === 403) {
      const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      const path = new URL(url, location.href).pathname;
      const apiPath = new URL(API, location.href).pathname.replace(/\/$/, "");
      if (path.startsWith(`${apiPath}/`) && !path.startsWith(`${apiPath}/auth/`)) {
        if (res.status === 401) window.dispatchEvent(new Event(LOGGED_OUT));
        // 403 은 거의 다 그 화면이 말할 일(관리자 전용 등)이다 — 2단계 등록 강제만 여기서 받는다.
        // 본문은 **복사본**으로 읽는다: 원본을 읽으면 그 화면이 빈 본문을 받는다.
        else
          res
            .clone()
            .json()
            .then((j: { error?: string }) => {
              if (j?.error === "totp_enrollment_required") window.dispatchEvent(new Event(ENROLL_REQUIRED));
            })
            .catch(() => undefined);
      }
    }
    return res;
  };
}

export const roleText = (r: Role) => tx(r === "admin" ? "common.role.admin" : "role.user");

/** 「이 설치 계정 · 7일(10-15 까지)」 처럼. 못 읽은 값은 「모름」이다. */
export function sessionText(me: Pick<Me, "via" | "expiresAt">): string {
  const door = tx(me.via === "local" ? "account.door.local" : me.via === "sso" ? "account.door.hub" : "account.door.unknown");
  if (!me.expiresAt) return tx("account.door.noEnd", { door });
  const d = new Date(me.expiresAt);
  const p = (n: number) => String(n).padStart(2, "0");
  return tx("account.door.until", { door, when: `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}` });
}

/** hub 로그인을 시작하는 주소 — api 가 state 를 쿠키에 두고 hub 로 보낸다(`/sso/login`). */
export const SSO_LOGIN_URL = `${API}/sso/login`;

/** hub 에서 돌아오다 실패한 갈래(`/#/?sso_error=…`, crates/api/src/sso.rs 의 `fail`). 서버는 갈래만
 *  싣고 문장은 여기서 쓴다 — 바깥에서 온 글자를 그대로 그리지 않는다. 모르는 갈래는 이름 그대로. */
export function ssoErrorText(kind: string, issuer: string | null): { head: string; body: string; tone: "warning" | "error" } {
  const hub = issuer ?? "hub";
  switch (kind) {
    case "state":
      return { tone: "warning", head: tx("auth.sso.state.head"), body: tx("auth.sso.state.body") };
    case "unreachable":
      return { tone: "error", head: tx("auth.sso.unreachable.head"), body: tx("auth.sso.unreachable.body", { hub }) };
    case "exchange":
      return { tone: "error", head: tx("auth.sso.exchange.head"), body: tx("auth.sso.exchange.body") };
    case "token":
      return { tone: "error", head: tx("auth.sso.token.head"), body: tx("auth.sso.token.body") };
    case "disabled":
      return { tone: "error", head: tx("auth.login.failed"), body: tx("auth.sso.disabled.body") };
    case "setup":
      return { tone: "warning", head: tx("auth.sso.setup.head"), body: tx("auth.sso.setup.body") };
    case "server":
      return { tone: "error", head: tx("auth.sso.server.head"), body: tx("auth.sso.server.body") };
    default:
      return { tone: "error", head: tx("auth.sso.unknownHead"), body: kind };
  }
}
